/* ==========================================================================
   Supply6 — cart freebie engine  (assets/s6-freebies.js)
   ==========================================================================

   WHAT THIS FILE DOES
   -------------------
   It keeps the free (₹0) gift lines in the cart correct. It watches the cart,
   works out which gifts the shopper has earned, and adds or removes those
   lines. It replaces the freebie behaviour the old GoKwik cart drawer gave us.

   THERE ARE NO PRODUCT IDS IN THIS FILE
   -------------------------------------
   Every rule lives in the theme editor:

     Shopify admin -> Online Store -> Themes -> Customize -> Theme settings
       -> "Supply6 Freebies"

   Each "Rule" slot there says: WHEN these products (optionally only these
   pack sizes) are in the cart, GIVE this ₹0 gift product, optionally only
   between a start and an end date. snippets/js-variables.liquid turns those
   settings into window.themeVariables.settings.s6Freebies and this file
   reads them. Changing a gift colour, adding a trigger product, running a
   promo for a weekend: all of that is a theme-settings change, no code edit.

   The cart-VALUE gifts ("spend ₹899, get a wafer") are a separate, older
   engine in assets/custom.js (addFreeProduct), configured under Theme
   settings -> Milestone Bar -> Milestone Base Free Gift. This file does not
   run that engine, but it does keep it honest (see next section).

   ONE GIFT PER FAMILY, EVER
   -------------------------
   The shop has several ₹0 versions of the same gift (Steel Pro Shaker Teal,
   Steel Pro Shaker Snow, ...). Theme settings -> Supply6 Freebies -> "One per
   family" lists words like "shaker, bottle". Any gift line whose product
   title contains one of those words belongs to that family, and the cart is
   never allowed to hold more than ONE line of a family, whoever added it:
   a rule here, the cart-value milestone in custom.js, an older page still
   sitting in a cache, or an old cart. Duplicates and left-overs are removed
   and any gift line at quantity 2+ is put back to 1.

   THE GIFT ARRIVES WITH THE PRODUCT, NOT AFTER IT
   -----------------------------------------------
   The gift line is added in the SAME /cart/add.js request as the product the
   shopper tapped, so the cart updates once and the drawer draws once. This
   works because we keep a copy of the last cart in memory, so the decision
   can be made on the spot, while the request is still being built.

   REMOVING A GIFT, AND ADDING IT BACK
   -----------------------------------
   Every gift row in the drawer has a small bin button. Tapping it:
     1. remembers "this gift was dismissed" in sessionStorage (browser tab
        memory only) BEFORE the removal request is sent, so this engine does
        not immediately put the gift back, and
     2. removes the line.
   The gift then reappears as an "Add back" row at the BOTTOM of the item
   list, but only while the cart still qualifies for it. Take the trigger
   product out of the cart and the row disappears too. "Add back" clears the
   flag and re-adds the gift. The memory is per browser tab and is also
   cleared automatically once the cart stops qualifying.

   WHERE EACH PIECE OF CART LOGIC LIVES
   ------------------------------------
   sections/mini-cart.liquid   the drawer's HTML and CSS. No JavaScript in it.
   snippets/s6-cart-line.liquid one row of the drawer. Decides which lines
                               look like gifts, prints the "Free · Qty 1"
                               pill and the bin button.
   snippets/js-variables.liquid turns Theme settings into the `s6Freebies`
                               object this file reads (rules, trigger variant
                               ids, gift products, family words).
   assets/custom.js            everything else about the cart (jQuery): open /
                               close, quantity changes, coupons, and the
                               cart-VALUE milestone gifts (addFreeProduct).
   assets/s6-freebies.js       this file.

   DEBUGGING
   ---------
   Theme settings -> Supply6 Freebies -> Developer -> "Log ... to console",
   or in the browser console: S6Freebies.rules(), S6Freebies.plan(cart),
   S6Freebies.families(), S6Freebies.reconcile().
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- CONFIG */
  /*
     Filled from Theme settings by applyThemeSettings() below. The values here
     are only what applies when a setting has never been saved.
  */
  var CONFIG = {
    enabled: true,
    // Words that group gift products into a family (one line per family).
    familyKeywords: ['shaker', 'bottle'],
    // Rules from the theme editor. Shape of one rule:
    //   { id, name, enabled, start, end, serverActive,
    //     triggerVariantIds: [...], triggerProductIds: [...],
    //     gift: { variantId, title } }
    rules: [],
    // Property written on every gift line this file adds.
    propertyName: '_s6_freebie',
    debug: false
  };

  var S6_ENGINE_ENABLED = true;

  /* ------------------------------------------------------------- UTILITIES */

  function log() {
    if (!CONFIG.debug || !window.console) return;
    console.log.apply(console, ['[s6-freebies]'].concat([].slice.call(arguments)));
  }

  function warn() {
    if (!window.console) return;
    console.warn.apply(console, ['[s6-freebies]'].concat([].slice.call(arguments)));
  }

  function toId(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? null : n;
  }

  function cleanTitle(title) {
    // "🎁 Steel Pro Shaker - Teal" -> "Steel Pro Shaker - Teal"
    return String(title || '').replace(/^[^A-Za-z0-9]+/, '').trim();
  }

  function giftLabel(title) {
    return 'Free ' + cleanTitle(title);
  }

  // A rule is in-window when now is between start and end (inclusive).
  // Parsing an ISO string that carries its own +05:30 offset gives the
  // correct absolute instant regardless of the shopper's own timezone.
  function ruleInWindow(rule, now) {
    if (rule.start) {
      var s = Date.parse(rule.start);
      if (!isNaN(s) && now < s) return false;
    }
    if (rule.end) {
      var e = Date.parse(rule.end);
      if (!isNaN(e) && now > e) return false;
    }
    return true;
  }

  /* ------------------------------------------------ THEME-SETTINGS INTAKE */

  function applyThemeSettings() {
    var s = window.themeVariables &&
            window.themeVariables.settings &&
            window.themeVariables.settings.s6Freebies;

    if (!s) {
      warn('no s6Freebies settings found on the page; no product rules will run');
      return;
    }

    if (typeof s.debug === 'boolean') CONFIG.debug = s.debug;
    if (typeof s.enabled === 'boolean') S6_ENGINE_ENABLED = s.enabled;

    if (typeof s.familyKeywords === 'string') {
      var words = s.familyKeywords.split(',').map(function (w) {
        return w.trim().toLowerCase();
      }).filter(Boolean);
      if (words.length) CONFIG.familyKeywords = words;
    }

    var rules = [];
    (s.rules || []).forEach(function (r, i) {
      if (!r) return;
      var gift = r.gift || {};
      var giftVid = toId(gift.variantId);
      var rule = {
        id: r.id || ('rule' + (i + 1)),
        name: r.name || ('Rule ' + (i + 1)),
        enabled: r.enabled !== false,
        start: (typeof r.start === 'string' && r.start.trim()) ? r.start.trim() : null,
        end: (typeof r.end === 'string' && r.end.trim()) ? r.end.trim() : null,
        // Liquid checked the same window against the shop clock when the page
        // was rendered, so a shopper with a shifted device clock cannot open a
        // promo early or keep it running late. Can lag by the page-cache time.
        serverActive: r.serverActive !== false,
        triggerVariantIds: (r.triggerVariantIds || []).map(toId).filter(function (v) { return v !== null; }),
        triggerProductIds: (r.triggerProductIds || []).map(toId).filter(function (v) { return v !== null; }),
        gift: giftVid ? { variantId: giftVid, title: gift.title || 'Free gift', price: toId(gift.price) } : null
      };

      // A rule with no trigger or no ₹0 gift is not usable. Say so in the
      // console (always, not only in debug) so a mis-set slot is noticed.
      if (!rule.gift) {
        if (rule.enabled && (rule.triggerVariantIds.length || rule.triggerProductIds.length)) {
          warn('rule "' + rule.name + '" is switched on but has no ₹0 gift product; ignored');
        }
        return;
      }
      if (rule.gift.price !== null && rule.gift.price !== 0) {
        warn('rule "' + rule.name + '" gift is not ₹0; ignored');
        return;
      }
      if (!rule.triggerVariantIds.length && !rule.triggerProductIds.length) {
        if (rule.enabled) warn('rule "' + rule.name + '" has no trigger products; ignored');
        return;
      }
      rules.push(rule);
    });
    CONFIG.rules = rules;
  }

  applyThemeSettings();

  /* ------------------------------------------------- GIFTS AND FAMILIES */

  // Every distinct gift product across all rules, keyed by variant id.
  // The key is what we write on the cart line as `_s6_freebie`.
  function giftKey(variantId) { return 'gift-' + toId(variantId); }

  function giftsByKey() {
    var out = {};
    CONFIG.rules.forEach(function (rule) {
      if (!rule.gift) return;
      var key = giftKey(rule.gift.variantId);
      if (!out[key]) out[key] = { key: key, variantId: rule.gift.variantId, title: rule.gift.title, label: giftLabel(rule.gift.title) };
    });
    return out;
  }

  function giftForKey(key) { return giftsByKey()[key] || null; }

  // Which family does a product title belong to? First matching keyword.
  function familyOfTitle(title) {
    var t = String(title || '').toLowerCase();
    for (var i = 0; i < CONFIG.familyKeywords.length; i++) {
      if (t.indexOf(CONFIG.familyKeywords[i]) !== -1) return CONFIG.familyKeywords[i];
    }
    return null;
  }

  function familyOfGiftKey(key) {
    var g = giftForKey(key);
    return g ? familyOfTitle(g.title) : null;
  }

  /* ----------------------------------------------- WHICH LINES ARE WHAT */

  function isFreebieLine(item) {
    return !!(item && item.properties && item.properties[CONFIG.propertyName]);
  }

  // Carts created before the GoKwik drawer was switched off still hold ₹0
  // freebie lines tagged with GoKwik's own properties.
  function isLegacyGokwikFreebieLine(item) {
    var p = (item && item.properties) || {};
    return !!(p._gokwik_freebie || p._gkfc);
  }

  // Lines added by the cart-value milestone engine in custom.js.
  function isMilestoneGiftLine(item) {
    var p = (item && item.properties) || {};
    return p._type === 'free_gift' || !!p._milestone_gift_level || !!p._free_gift_of;
  }

  function isAnyGiftLine(item) {
    return isFreebieLine(item) || isLegacyGokwikFreebieLine(item) || isMilestoneGiftLine(item);
  }

  // A line counts towards a family only when it is a gift: a ₹0 line, or one
  // carrying any gift property. A PAID product that happens to be called
  // "shaker" is never touched.
  function lineFamily(item) {
    if (!item) return null;
    if (item.price !== 0 && !isAnyGiftLine(item)) return null;
    return familyOfTitle(item.product_title || item.title);
  }

  // Our own gift key for a line: the property we wrote, or (for a legacy
  // GoKwik line or any bare ₹0 line) the configured gift with that variant.
  // Milestone lines from custom.js are deliberately NOT ours.
  function freebieKeyForLine(item) {
    if (!item) return null;
    var props = item.properties || {};
    if (props[CONFIG.propertyName]) return giftKey(item.variant_id);
    if (isMilestoneGiftLine(item)) return null;
    if (!isLegacyGokwikFreebieLine(item) && item.price !== 0) return null;
    var key = giftKey(item.variant_id);
    return giftForKey(key) ? key : null;
  }

  /* ---------------------------------------------------------- RULE LOGIC */

  function ruleLive(rule, now) {
    if (rule.enabled === false) return false;
    if (rule.serverActive === false) return false;
    return ruleInWindow(rule, now);
  }

  // Does this rule fire for a given product / variant? Either id may be null:
  // a cart line knows both, an add-to-cart request only knows the variant.
  function ruleHits(rule, productId, variantId) {
    if (productId !== null && rule.triggerProductIds.indexOf(productId) !== -1) return true;
    if (variantId !== null && rule.triggerVariantIds.indexOf(variantId) !== -1) return true;
    return false;
  }

  // Which rules are currently satisfied by the cart?
  function activeRuleIds(cart) {
    var now = Date.now();
    var ids = {};
    (cart.items || []).forEach(function (item) {
      if (isAnyGiftLine(item)) return;          // gifts never trigger gifts
      var pid = toId(item.product_id);
      var vid = toId(item.variant_id);
      CONFIG.rules.forEach(function (rule) {
        if (ids[rule.id]) return;
        if (!ruleLive(rule, now)) return;
        if (ruleHits(rule, pid, vid)) ids[rule.id] = true;
      });
    });
    return ids;
  }

  // The set of gift keys the cart *should* contain, one line each, qty 1.
  // Two rules granting the same gift collapse to one line.
  function desiredGifts(activeIds) {
    var wanted = {};
    CONFIG.rules.forEach(function (rule) {
      if (activeIds[rule.id] && rule.gift) wanted[giftKey(rule.gift.variantId)] = true;
    });
    return wanted;
  }

  // Families already covered by an active rule (whether or not the shopper
  // has dismissed that gift). A covered family gets nothing from custom.js.
  function coveredFamilies(activeIds) {
    var fams = {};
    Object.keys(desiredGifts(activeIds)).forEach(function (key) {
      var f = familyOfGiftKey(key);
      if (f) fams[f] = true;
    });
    return fams;
  }

  /* --------------------------------------------------- DISMISSED GIFTS ---- */
  /*
     When a shopper removes a free gift on purpose we must not put it straight
     back. The gift's key is stored in sessionStorage (this browser tab only).
     Storage can throw (private mode, blocked cookies) so every access is
     wrapped: the worst case is that the dismissal is simply not remembered.
  */
  var DISMISS_KEY = 's6_freebies_dismissed';
  var dismissedCache = {};

  function readDismissed() {
    try {
      var raw = window.sessionStorage.getItem(DISMISS_KEY);
      var obj = raw ? JSON.parse(raw) : null;
      dismissedCache = (obj && typeof obj === 'object') ? obj : {};
    } catch (e) { /* storage unavailable: keep the in-memory copy */ }
    return dismissedCache;
  }

  function writeDismissed(map) {
    dismissedCache = map || {};
    try { window.sessionStorage.setItem(DISMISS_KEY, JSON.stringify(dismissedCache)); }
    catch (e) { /* not persisted; in-memory copy still works for this page */ }
  }

  function isDismissed(key) { return !!(key && dismissedCache[key]); }

  function setDismissed(key, on) {
    if (!key) return;
    var map = readDismissed();
    if (on) { map[key] = 1; } else { delete map[key]; }
    writeDismissed(map);
  }

  readDismissed();

  /* ------------------------------------------- MILESTONE GIFTS (custom.js) */
  /*
     assets/custom.js gives a gift when the cart value passes a milestone
     (Theme settings > Milestone Bar > Milestone Base Free Gift). Those lines
     carry `_type: "free_gift"`. We give them the keys "milestone-1" /
     "milestone-2" so they can be dismissed and added back like our own.
  */
  function milestoneGift(n) {
    var s = window.themeVariables && window.themeVariables.settings;
    if (!s) return null;
    var p = s['freeProduct_' + n];
    var vid = p && p.variants && p.variants[0] && toId(p.variants[0].id);
    if (!vid) return null;
    return {
      key: 'milestone-' + n,
      n: n,
      variantId: vid,
      title: p.title || 'Free gift',
      label: giftLabel(p.title || 'Free gift'),
      family: familyOfTitle(p.title),
      // The theme setting is in rupees; the cart is in paise.
      thresholdPaise: (parseInt(s['freeProduct_milestone_' + n] || '0', 10) || 0) * 100
    };
  }

  function milestoneGifts() {
    var out = [];
    [1, 2].forEach(function (n) { var g = milestoneGift(n); if (g) out.push(g); });
    return out;
  }

  function milestoneGiftByKey(key) {
    var found = null;
    milestoneGifts().forEach(function (g) { if (g.key === key) found = g; });
    return found;
  }

  // The current milestone variant for a family (null if none is a gift there).
  function milestoneVariantForFamily(family) {
    var found = null;
    milestoneGifts().forEach(function (g) {
      if (found === null && g.family && g.family === family) found = g.variantId;
    });
    return found;
  }

  // Does the cart currently earn this milestone gift?
  function milestoneEarned(cart, key, covered) {
    var g = milestoneGiftByKey(key);
    if (!g || !g.thresholdPaise) return false;
    if (g.family && covered[g.family]) return false;   // a rule already gives one
    var total = cart.original_total_price || cart.total_price || 0;
    return total >= g.thresholdPaise;
  }

  /* --------------------------------- KEEP THE MILESTONE ENGINE HONEST */
  /*
     custom.js's addFreeProduct identifies its gifts purely by variant id, so
     left alone it would add a Teal shaker next to our Snow one, or next to a
     stale Snow one from an older page. We wrap it: for the duration of each
     call, a milestone gift is hidden from it whenever its family is already
     covered - by an active rule, by any gift line of that family that is not
     its own current line, or because the shopper dismissed it. Hiding stops
     its add AND its remove, so it can never fight the reconciler.
  */
  var lastCovered = {};

  function patchMilestoneEngine() {
    var original = window.addFreeProduct;
    if (typeof original !== 'function' || original.__s6Patched) return false;

    var patched = function (event, cart) {
      var settings = window.themeVariables && window.themeVariables.settings;
      var stash = {};
      var liveCart = (cart && cart.items) ? cart : cachedCart;
      var covered = {};
      var familyLines = {};

      if (liveCart && liveCart.items) {
        covered = coveredFamilies(activeRuleIds(liveCart));
        liveCart.items.forEach(function (item) {
          var f = lineFamily(item);
          if (!f) return;
          if (!familyLines[f]) familyLines[f] = [];
          familyLines[f].push(item);
        });
      }
      lastCovered = covered;

      if (settings) {
        milestoneGifts().forEach(function (g) {
          var field = 'freeProduct_' + g.n;
          var hide = false;
          var why = '';

          if (g.family && covered[g.family]) { hide = true; why = 'family covered by a rule'; }

          if (!hide && g.family && familyLines[g.family]) {
            // Any line of this family that is not custom.js's own current
            // line means the family is already served.
            familyLines[g.family].forEach(function (item) {
              if (isMilestoneGiftLine(item) && toId(item.variant_id) === g.variantId) return;
              hide = true; why = 'family already in cart';
            });
          }

          if (!hide && isDismissed(g.key)) { hide = true; why = 'dismissed by shopper'; }

          if (hide) {
            stash[field] = settings[field];
            settings[field] = null;
            log('milestone gift', g.n, 'hidden for this pass:', why);
          }
        });
      }

      try {
        return original.apply(this, arguments);
      } finally {
        Object.keys(stash).forEach(function (field) { settings[field] = stash[field]; });
      }
    };

    patched.__s6Patched = true;
    window.addFreeProduct = patched;
    log('milestone engine patched');
    return true;
  }

  /* ----------------------------------------------- DRAWER PRESENTATION ---- */

  var STYLE_ID = 's6-freebies-style';

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    // The pill, bin button and "Add back" row are styled in
    // sections/mini-cart.liquid. These two rules are only a safety net for
    // the moment before the drawer re-renders.
    var css = [
      '.s6-freebie-line .quantity-selector,',
      '.s6-freebie-line .line-item__remove-button { display: none !important; }',
      '.line-item--gift .quantity-selector { display: none !important; }'
    ].join('\n');
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(el);
  }

  function decorateLines(cart) {
    var lists = document.querySelectorAll('.mini-cart-line-items');
    if (!lists.length || !cart || !cart.items) return;

    // Match each rendered row to its cart line by the key PRINTED ON THE ROW
    // (sections/mini-cart.liquid prints data-line-key), never by position.
    var byKey = {};
    var byVariant = {};
    cart.items.forEach(function (item) {
      byKey[item.key] = item;
      if (!byVariant[item.variant_id]) byVariant[item.variant_id] = item;
    });

    Array.prototype.forEach.call(lists, function (list) {
      var nodes = list.querySelectorAll('line-item');
      Array.prototype.forEach.call(nodes, function (el) {
        var item = byKey[el.getAttribute('data-line-key')];
        if (!item) item = byVariant[el.getAttribute('data-variant-id')];
        if (!item) return;
        var key = freebieKeyForLine(item);
        el.setAttribute('data-s6-line-key', item.key);
        if (key) {
          el.classList.add('line-item--gift', 's6-freebie-line');
          el.setAttribute('data-s6-freebie', key);
        } else if (isMilestoneGiftLine(item)) {
          el.classList.add('line-item--gift', 's6-freebie-line');
          el.setAttribute('data-s6-milestone-gift', item.variant_id);
        } else {
          el.classList.remove('s6-freebie-line');
          el.removeAttribute('data-s6-freebie');
          el.removeAttribute('data-s6-milestone-gift');
        }
      });
    });
  }

  /* --------------------------------------------------- PROGRESS BAR TWEAKS */
  /*
     When a rule already grants a gift of some family, show the milestone
     segment for that family as unlocked rather than dangling an unreachable
     reward. Purely cosmetic and re-applied after every re-render.
  */
  function markCoveredMilestonesUnlocked(covered) {
    var nodes = document.querySelectorAll('.reward-bar__segment, .shipiing-progress-track .check-point');
    Array.prototype.forEach.call(nodes, function (node) {
      var text = (node.textContent || '').toLowerCase();
      var fam = null;
      CONFIG.familyKeywords.forEach(function (w) { if (fam === null && text.indexOf(w) !== -1) fam = w; });
      if (!fam) return;
      if (covered[fam]) {
        node.classList.add('is-unlocked', 'has_magenta', 's6-freebie-unlocked');
      } else if (node.classList.contains('s6-freebie-unlocked')) {
        node.classList.remove('is-unlocked', 'has_magenta', 's6-freebie-unlocked');
      }
    });
  }

  /* ------------------------------------------------------- RECONCILIATION  */

  var chain = Promise.resolve();   // serialises every cart write we make
  var busy  = false;              // true while our own writes are in flight
  var lastCart = null;            // most recent cart we reconciled against
  var cachedCart = null;          // synchronous copy for the co-add decision
  var lastEarned = {};            // gift keys the cart currently qualifies for
  var s6OwnWrite = false;         // true only while WE are talking to the cart

  function s6Post(url, payload, tag) {
    s6OwnWrite = true;
    var request;
    try {
      request = fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
    } finally {
      s6OwnWrite = false;
    }
    return request.then(function (r) {
      if (!r.ok) log(tag + ' failed', r.status);
      return r;
    }).catch(function (e) { log(tag + ' error', e); });
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { cachedCart = cart; return cart; });
  }

  function addItems(items) {
    if (!items.length) return Promise.resolve();
    return s6Post('/cart/add.js', { items: items }, 'add');
  }

  function giftItem(key) {
    var g = giftForKey(key);
    var props = {};
    props[CONFIG.propertyName] = key;
    return { id: g.variantId, quantity: 1, properties: props };
  }

  // Keyed by LINE key, so two lines holding the same variant can be treated
  // separately. /cart/update.js accepts line keys.
  function updateLines(updates) {
    if (!Object.keys(updates).length) return Promise.resolve();
    return s6Post('/cart/update.js', { updates: updates }, 'update');
  }

  function setLineQuantity(key, quantity) {
    var updates = {};
    updates[key] = quantity;
    return updateLines(updates);
  }

  // Work out the difference between what the cart has and what it should
  // have. Returns null when nothing needs doing (the loop guard).
  function planFor(cart) {
    var activeIds = activeRuleIds(cart);
    var earned    = desiredGifts(activeIds);
    var covered   = coveredFamilies(activeIds);

    // A gift the shopper deliberately removed stays out until "Add back".
    var wanted = {};
    Object.keys(earned).forEach(function (key) {
      if (!isDismissed(key)) wanted[key] = true;
    });

    var toAdd = [], toRemove = [], toFix = [], seen = {};

    function markRemove(item, why) {
      if (toRemove.indexOf(item.key) !== -1) return;
      toRemove.push(item.key);
      log('remove', why, item.product_title || item.title, item.properties);
    }
    function markFix(item) {
      if (item.quantity !== 1 && toRemove.indexOf(item.key) === -1 && toFix.indexOf(item.key) === -1) toFix.push(item.key);
    }

    // 1. OUR gift lines (and legacy / bare ₹0 lines of a configured gift),
    //    grouped by gift. Keep one line per wanted gift, drop the rest.
    var groups = {};
    (cart.items || []).forEach(function (item) {
      var key = freebieKeyForLine(item);
      if (!key) return;
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    Object.keys(groups).forEach(function (key) {
      var lines = groups[key];
      var keeper = null;
      lines.forEach(function (it) { if (!keeper && isFreebieLine(it)) keeper = it; });
      if (!keeper) keeper = lines[0];
      var keep = !!wanted[key] && !!giftForKey(key);
      lines.forEach(function (it) {
        if (!keep || it !== keeper) { markRemove(it, keep ? 'duplicate gift' : 'gift not earned'); return; }
        seen[key] = true;
        markFix(it);
      });
    });
    Object.keys(wanted).forEach(function (key) { if (!seen[key]) toAdd.push(key); });

    // 2. ONE PER FAMILY. Look at every gift line by family (shaker, bottle,
    //    ...), including the milestone lines custom.js owns.
    var families = {};
    (cart.items || []).forEach(function (item) {
      if (toRemove.indexOf(item.key) !== -1) return;
      var f = lineFamily(item);
      if (!f) return;
      if (!families[f]) families[f] = [];
      families[f].push(item);
    });

    Object.keys(families).forEach(function (f) {
      var lines = families[f];
      var keeper = null;

      if (covered[f]) {
        // A rule gives this family. Only our (kept) line may stay; the rule's
        // gift is in toAdd already if it is missing.
        lines.forEach(function (it) { if (!keeper && isFreebieLine(it) && seen[freebieKeyForLine(it)]) keeper = it; });
      } else {
        // No rule gives this family. custom.js may: its CURRENT variant line
        // stays (custom.js removes it itself when the cart drops below the
        // milestone). Anything else - a stale colour from an older setting or
        // an older cached page, a legacy line, a bare ₹0 gift - goes.
        var current = milestoneVariantForFamily(f);
        lines.forEach(function (it) {
          if (!keeper && isMilestoneGiftLine(it) && current !== null && toId(it.variant_id) === current) keeper = it;
        });
      }

      lines.forEach(function (it) {
        if (it === keeper) { markFix(it); return; }
        if (isFreebieLine(it) && seen[freebieKeyForLine(it)] && keeper === null) {
          // our kept line in a family custom.js does not serve: fine, keep it
          markFix(it); return;
        }
        markRemove(it, 'second ' + f + ' in cart');
      });
    });

    // 3. Any milestone gift outside a family (e.g. wafer) at quantity 2+.
    (cart.items || []).forEach(function (it) {
      if (isMilestoneGiftLine(it)) markFix(it);
    });

    if (!toAdd.length && !toRemove.length && !toFix.length) return null;
    return { toAdd: toAdd, toRemove: toRemove, toFix: toFix };
  }

  // At most TWO requests for any amount of tidying up: one update, one add.
  function applyPlan(plan) {
    var updates = {};
    plan.toRemove.forEach(function (key) { updates[key] = 0; });
    plan.toFix.forEach(function (key) { updates[key] = 1; });
    return updateLines(updates).then(function () {
      return addItems(plan.toAdd.map(giftItem));
    });
  }

  function reconcile(cartFromEvent) {
    chain = chain.then(function () {
      if (busy) return;
      busy = true;

      return Promise.resolve(cartFromEvent || fetchCart())
        .then(function (cart) {
          if (!cart || !cart.items) return null;

          var activeIds = activeRuleIds(cart);
          var covered   = coveredFamilies(activeIds);
          lastCart = cart;
          cachedCart = cart;
          lastEarned = desiredGifts(activeIds);
          lastCovered = covered;
          forgetStaleDismissals(cart, lastEarned, covered);
          markCoveredMilestonesUnlocked(covered);
          decorateLines(cart);
          renderGiftBackSlot(cart, lastEarned, covered);

          var plan = planFor(cart);
          if (!plan) { log('cart already correct'); return null; }

          log('applying', plan);
          return applyPlan(plan).then(function () { return true; });
        })
        .then(function (changed) {
          if (!changed) return;
          // Re-render the drawer through the theme's own helper so every
          // other listener (custom.js included) sees a consistent cart.
          if (typeof window.themeRefreshCart === 'function') {
            return window.themeRefreshCart(false);
          }
          return fetchCart().then(function (cart) {
            document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
              bubbles: true, detail: { cart: cart }
            }));
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
              bubbles: true, detail: { cart: cart, openMiniCart: false }
            }));
          });
        })
        .catch(function (e) { log('reconcile error', e); })
        .then(function () { busy = false; });
    });
    return chain;
  }

  /* ------------------------------------------- "YOUR FREE GIFT" ADD-BACK ROW */

  var SLOT_CLASS = 's6-giftback';

  // Forget a dismissal once the cart stops qualifying, so adding the trigger
  // product again simply grants the gift again.
  function forgetStaleDismissals(cart, earned, covered) {
    var map = readDismissed();
    var changed = false;
    Object.keys(map).forEach(function (key) {
      var stillEarned = key.indexOf('milestone-') === 0
        ? milestoneEarned(cart, key, covered)
        : !!earned[key];
      if (!stillEarned) { delete map[key]; changed = true; }
    });
    if (changed) writeDismissed(map);
  }

  function giftBackOffers(cart, earned, covered) {
    var offers = [];
    Object.keys(earned).forEach(function (key) {
      if (!isDismissed(key)) return;
      var g = giftForKey(key);
      if (g) offers.push({ key: key, label: g.label });
    });
    milestoneGifts().forEach(function (g) {
      if (!isDismissed(g.key)) return;
      if (!milestoneEarned(cart, g.key, covered)) return;
      offers.push({ key: g.key, label: g.label });
    });
    return offers;
  }

  function renderGiftBackSlot(cart, earned, covered) {
    var lists = document.querySelectorAll('.mini-cart-line-items');
    if (!lists.length || !cart) return;

    var offers = giftBackOffers(cart, earned || {}, covered || {});
    var signature = offers.map(function (o) { return o.key; }).join('|');

    Array.prototype.forEach.call(lists, function (list) {
      var old = list.querySelectorAll('.' + SLOT_CLASS);
      // Already showing exactly these offers? Leave the DOM alone, or the
      // re-render watcher below would wake up and we would loop.
      if (list.__s6Slot === signature && old.length === offers.length) return;
      list.__s6Slot = signature;

      Array.prototype.forEach.call(old, function (n) { if (n.parentNode) n.parentNode.removeChild(n); });

      offers.forEach(function (offer) {
        var row = document.createElement('div');
        row.className = SLOT_CLASS;
        var text = document.createElement('div');
        text.className = 's6-giftback__text';
        var strong = document.createElement('b');
        strong.appendChild(document.createTextNode('Your free gift'));
        text.appendChild(strong);
        text.appendChild(document.createTextNode(offer.label + ' — yours free with this order'));
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 's6-giftback__add';
        btn.setAttribute('data-s6-gift-add', offer.key);
        btn.appendChild(document.createTextNode('Add back'));
        row.appendChild(text);
        row.appendChild(btn);
        list.appendChild(row);
      });
    });
  }

  // The drawer re-renders by replacing its own HTML, which wipes everything
  // we drew. One cart change can cause more than one re-render (custom.js
  // runs its milestone pass straight after ours), so re-apply on a short
  // ladder of delays as well as immediately.
  function reapplyDecorations() {
    if (!lastCart) return;
    injectStyles();
    decorateLines(lastCart);
    markCoveredMilestonesUnlocked(lastCovered);
    renderGiftBackSlot(lastCart, lastEarned, lastCovered);
  }

  var reapplyTimers = [];
  function reapplySoon() {
    reapplyTimers.forEach(function (t) { clearTimeout(t); });
    reapplyTimers = [120, 400, 900, 1800].map(function (ms) {
      return setTimeout(reapplyDecorations, ms);
    });
    if (window.requestAnimationFrame) window.requestAnimationFrame(reapplyDecorations);
    else reapplyDecorations();
  }

  /* ------------------------------------------------ REMOVE / ADD-BACK CLICKS */

  function refreshDrawer() {
    if (typeof window.themeRefreshCart === 'function') return window.themeRefreshCart(false);
    return fetchCart().then(function (cart) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true, detail: { cart: cart, openMiniCart: false }
      }));
    });
  }

  // Which gift is this row? Our own rows carry data-s6-freebie; milestone
  // rows carry data-s6-milestone-gift (the variant id).
  function giftKeyForRow(row) {
    var key = row.getAttribute('data-s6-freebie');
    if (key) return giftForKey(key) ? key : giftKey(row.getAttribute('data-variant-id'));

    var match = null;
    var vid = toId(row.getAttribute('data-s6-milestone-gift'));
    if (vid !== null) {
      milestoneGifts().forEach(function (g) { if (g.variantId === vid) match = g.key; });
      if (match) return match;
    }
    vid = toId(row.getAttribute('data-variant-id'));
    if (vid === null) return null;
    if (giftForKey(giftKey(vid))) return giftKey(vid);
    milestoneGifts().forEach(function (g) { if (g.variantId === vid) match = g.key; });
    return match;
  }

  function handleGiftRemove(btn) {
    var row = btn.closest ? btn.closest('line-item') : null;
    if (!row) return;

    // Mark it dismissed BEFORE the removal request goes out.
    setDismissed(giftKeyForRow(row), true);

    var lineKey = row.getAttribute('data-line-key') || row.getAttribute('data-s6-line-key');
    btn.disabled = true;

    chain = chain.then(function () {
      if (lineKey) return setLineQuantity(lineKey, 0);
      var link = row.querySelector('[data-remove-link]');
      if (link) link.click();
      return null;
    }).then(function () {
      if (lineKey) return refreshDrawer();
      return null;
    }).then(function () {
      reapplySoon();
    }).catch(function (e) { log('gift remove failed', e); });

    return chain;
  }

  function handleGiftAddBack(key, btn) {
    if (!key) return;
    setDismissed(key, false);
    if (btn) btn.disabled = true;

    var variantId = null;
    var props = null;

    var g = giftForKey(key);
    if (g) {
      variantId = g.variantId;
      props = {};
      props[CONFIG.propertyName] = key;
    } else {
      var m = milestoneGiftByKey(key);
      if (m) {
        variantId = m.variantId;
        props = { '_type': 'free_gift' };   // same property custom.js writes
      }
    }
    if (!variantId) return;

    chain = chain.then(function () {
      return addItems([{ id: variantId, quantity: 1, properties: props }]);
    }).then(function () {
      return refreshDrawer();
    }).then(function () {
      reapplySoon();
    }).catch(function (e) { log('gift add back failed', e); });

    return chain;
  }

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || target.nodeType !== 1 || !target.closest) return;

    var removeBtn = target.closest('[data-s6-gift-remove]');
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleGiftRemove(removeBtn);
      return;
    }
    var addBtn = target.closest('[data-s6-gift-add]');
    if (addBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleGiftAddBack(addBtn.getAttribute('data-s6-gift-add'), addBtn);
    }
  }, true);

  /* ------------------------------------------- ADD THE GIFT WITH THE PRODUCT */
  /*
     When the shopper adds a product that earns a gift, we slip the gift into
     the SAME /cart/add.js request. The decision is made synchronously from
     `cachedCart` (refreshed after every cart change) plus the items in the
     request itself.
  */

  function isCartAddUrl(url) {
    return typeof url === 'string' && url.indexOf('/cart/add') !== -1;
  }

  // Turn "items[0][id]" style keys into a nested object.
  function setDeep(target, path, value) {
    var parts = path.replace(/\]/g, '').split('[');
    var node = target;
    for (var i = 0; i < parts.length - 1; i++) {
      if (typeof node[parts[i]] !== 'object' || node[parts[i]] === null) node[parts[i]] = {};
      node = node[parts[i]];
    }
    node[parts[parts.length - 1]] = value;
  }

  function formStringToObject(str) {
    var out = {};
    String(str).split('&').forEach(function (pair) {
      if (!pair) return;
      var bits = pair.split('=');
      var k = decodeURIComponent(bits[0].replace(/\+/g, ' '));
      var v = decodeURIComponent((bits[1] || '').replace(/\+/g, ' '));
      setDeep(out, k, v);
    });
    return out;
  }

  // Read the items out of an add-to-cart payload, whatever shape it arrives in.
  function readAddItems(data) {
    if (!data) return null;
    if (typeof data === 'string') {
      var head = data.replace(/^\s+/, '').charAt(0);
      if (head === '{' || head === '[') {
        try { return readAddItems(JSON.parse(data)); } catch (e) { return null; }
      }
      return readAddItems(formStringToObject(data));
    }
    if (typeof data !== 'object') return null;
    if (Object.prototype.toString.call(data.items) === '[object Array]') return data.items.slice();
    if (data.items && typeof data.items === 'object') {
      return Object.keys(data.items).sort().map(function (k) { return data.items[k]; });
    }
    if (data.id) {
      var one = { id: data.id, quantity: data.quantity };
      if (data.properties && typeof data.properties === 'object') one.properties = data.properties;
      return [one];
    }
    return null;
  }

  function cleanItem(it) {
    var q = (it.quantity === undefined || it.quantity === null || it.quantity === '') ? 1 : it.quantity;
    var out = { id: it.id, quantity: q };
    if (it.properties && typeof it.properties === 'object' &&
        Object.keys(it.properties).length) out.properties = it.properties;
    return out;
  }

  function cartHasVariant(cart, variantId) {
    var vid = toId(variantId);
    return (cart.items || []).some(function (item) { return toId(item.variant_id) === vid; });
  }

  // Which gifts should ride along with the items being added?
  function giftsForAdd(incomingItems) {
    if (!S6_ENGINE_ENABLED) return [];
    var cart = cachedCart;
    if (!cart || !cart.items) return [];      // no cached cart yet: the ordinary pass will do it
    var now = Date.now();
    var wanted = {};

    CONFIG.rules.forEach(function (rule) {
      if (!ruleLive(rule, now) || !rule.gift) return;
      var hit = false;
      (cart.items || []).forEach(function (item) {
        if (hit || isAnyGiftLine(item)) return;
        if (ruleHits(rule, toId(item.product_id), toId(item.variant_id))) hit = true;
      });
      incomingItems.forEach(function (it) {
        if (hit) return;
        if (ruleHits(rule, null, toId(it.id))) hit = true;
      });
      if (hit) wanted[giftKey(rule.gift.variantId)] = true;
    });

    var out = [];
    Object.keys(wanted).forEach(function (key) {
      if (isDismissed(key)) return;
      var g = giftForKey(key);
      if (!g) return;
      if (cartHasVariant(cart, g.variantId)) return;
      var already = incomingItems.some(function (it) { return toId(it.id) === toId(g.variantId); });
      if (already) return;
      out.push(giftItem(key));
    });
    return out;
  }

  function rewriteAddPayload(original, items, extra) {
    var merged = items.concat(extra).map(cleanItem);
    if (typeof original === 'string') {
      var head = original.replace(/^\s+/, '').charAt(0);
      if (head === '{' || head === '[') return JSON.stringify({ items: merged });
      return merged.map(function (it, i) {
        var parts = ['items[' + i + '][id]=' + encodeURIComponent(it.id),
                     'items[' + i + '][quantity]=' + encodeURIComponent(it.quantity || 1)];
        var props = it.properties || {};
        Object.keys(props).forEach(function (p) {
          parts.push('items[' + i + '][properties][' + encodeURIComponent(p) + ']=' +
                     encodeURIComponent(props[p]));
        });
        return parts.join('&');
      }).join('&');
    }
    return { items: merged };
  }

  function maybeAppendGifts(original) {
    if (s6OwnWrite) return null;
    if (typeof FormData !== 'undefined' && original instanceof FormData) return null;
    var items = readAddItems(original);
    if (!items || !items.length) return null;
    var extra = giftsForAdd(items);
    if (!extra.length) return null;
    log('adding', extra.length, 'gift(s) in the same request as the product');
    return rewriteAddPayload(original, items, extra);
  }

  // jQuery: prefilters run before jQuery turns `data` into a query string.
  if (window.jQuery && window.jQuery.ajaxPrefilter) {
    window.jQuery.ajaxPrefilter(function (options) {
      try {
        if (!isCartAddUrl(options.url)) return;
        var rewritten = maybeAppendGifts(options.data);
        if (rewritten !== null) options.data = rewritten;
      } catch (e) { log('prefilter error', e); }
    });
  }

  /* -------------------------------------------------------------- TRIGGERS */

  var scheduled = null;
  function schedule(cart) {
    clearTimeout(scheduled);
    // Several cart events often arrive together for one shopper action.
    scheduled = setTimeout(function () { reconcile(cart); }, 150);
  }

  ['cart:updated', 'cart:refresh'].forEach(function (name) {
    var handler = function (event) {
      var cart = event && event.detail && event.detail.cart;
      reapplySoon();
      schedule(cart || null);
    };
    document.documentElement.addEventListener(name, handler);
    document.addEventListener(name, handler);
  });

  // Safety net: catch cart writes made by code that never fires an event.
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) { url = ''; }

      var mine = s6OwnWrite || busy;
      var args = arguments;

      if (!mine && isCartAddUrl(url) && init && init.body) {
        try {
          var rewritten = maybeAppendGifts(init.body);
          if (rewritten !== null) {
            var copy = {};
            for (var k in init) { if (Object.prototype.hasOwnProperty.call(init, k)) copy[k] = init[k]; }
            copy.body = rewritten;
            args = [input, copy];
          }
        } catch (e) { log('fetch rewrite error', e); }
      }

      var result = nativeFetch.apply(this, args);
      if (!mine && /\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
        result.then(function () { schedule(null); }).catch(function () {});
      }
      return result;
    };
  }

  if (window.jQuery) {
    window.jQuery(document).ajaxComplete(function (event, xhr, settings) {
      if (busy || !settings || !settings.url) return;
      if (/\/cart\/(add|change|update|clear)(\.js)?/.test(settings.url)) schedule(null);
    });
  }

  /* ----------------------------------------------------------------- BOOT  */

  var watchTries = 0;
  function watchForRerenders() {
    if (!window.MutationObserver) return;
    var drawer = document.getElementById('mini-cart');
    if (!drawer) {
      if (watchTries++ < 40) setTimeout(watchForRerenders, 250);
      return;
    }
    if (drawer.__s6Watched) return;
    drawer.__s6Watched = true;
    var queued = false;
    var observer = new MutationObserver(function () {
      if (queued || !lastCart) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        reapplyDecorations();
      });
    });
    observer.observe(drawer, { childList: true, subtree: true });
  }

  function boot() {
    // Master switch (Theme settings > Supply6 Freebies). Off means no styles,
    // no patching of the milestone engine, no observers, no cart writes.
    if (!S6_ENGINE_ENABLED) {
      log('disabled in Theme settings > Supply6 Freebies; doing nothing');
      return;
    }
    injectStyles();
    if (!patchMilestoneEngine()) {
      var tries = 0;
      var t = setInterval(function () {
        if (patchMilestoneEngine() || ++tries > 20) clearInterval(t);
      }, 100);
    }
    watchForRerenders();
    // Prime the cached cart so the very first add-to-cart can carry its gift.
    reconcile(null);
  }

  // Debug handle for the browser console.
  window.S6Freebies = {
    enabled: S6_ENGINE_ENABLED,
    config: CONFIG,
    rules: function () { return CONFIG.rules; },
    gifts: giftsByKey,
    families: function () { return CONFIG.familyKeywords.slice(); },
    reconcile: function () { return reconcile(null); },
    plan: planFor,
    activeRules: activeRuleIds,
    desired: desiredGifts,
    covered: function (cart) { return coveredFamilies(activeRuleIds(cart || cachedCart || { items: [] })); },
    dismissed: readDismissed,
    undismissAll: function () { writeDismissed({}); return reconcile(null); },
    offers: function () { return lastCart ? giftBackOffers(lastCart, lastEarned, lastCovered) : []; },
    milestones: milestoneGifts,
    redraw: reapplyDecorations,
    coAdd: giftsForAdd,
    cart: function () { return cachedCart; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
